import React, { useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, ChevronDown, Search } from "lucide-react";
import { Panel } from "../components/ui";
import Disclaimer from "../components/Disclaimer";
import StockPrimer from "../components/StockPrimer";
import { useTranslation } from "react-i18next";
import useSeo from "../lib/useSeo";
import { useAuth } from "../context/AuthContext";

// Article bodies live in the i18n files so the whole knowledge base is translated
// alongside the rest of the UI rather than being an English-only island.
const SECTIONS = [
  { id: "start", articles: ["whatIsCo Stock", "firstBuy", "fifo", "brokers"] },
  { id: "portfolio", articles: ["holdings", "fees", "currency", "risk"] },
  // Only listed while their product area is open (backend/features.py).
  { id: "investorsTopic", articles: ["following", "whatYouSee", "leaderboard", "comments"], feature: "community" },
  { id: "billingTopic", articles: ["subscription", "invoices", "cancel"], feature: "payments" },
  { id: "taxTopic", articles: ["abgeltung", "teilfreistellung", "cryptoTax"] },
];

function Article({ sectionId, articleId, t }) {
  const [open, setOpen] = useState(false);
  const title = t(`kb.${sectionId}.${articleId}.q`);
  const body = t(`kb.${sectionId}.${articleId}.a`);
  return (
    <div className="border-b border-slate-50 last:border-0" data-testid={`kb-${articleId}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 py-3.5 text-left"
      >
        <span className="font-medium text-sm text-slate-800">{title}</span>
        <ChevronDown size={16} className={`text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="pb-4 text-sm text-slate-600 leading-relaxed whitespace-pre-line">{body}</p>}
    </div>
  );
}

export default function KnowledgeBase() {
  const { t } = useTranslation();
  const { features } = useAuth();
  const sections = SECTIONS.filter((s) => !s.feature || features[s.feature]);
  const [q, setQ] = useState("");

  // FAQPage markup makes these answers eligible for rich results and gives AI
  // assistants clean question/answer pairs to quote.
  useSeo({
    title: t("kb.title"),
    description: t("seo.kbDesc"),
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: sections.flatMap((s) => s.articles.map((a) => ({
        "@type": "Question",
        name: t(`kb.${s.id}.${a}.q`),
        acceptedAnswer: { "@type": "Answer", text: t(`kb.${s.id}.${a}.a`) },
      }))),
    },
  });

  const term = q.trim().toLowerCase();
  const matches = (sectionId, articleId) =>
    !term ||
    t(`kb.${sectionId}.${articleId}.q`).toLowerCase().includes(term) ||
    t(`kb.${sectionId}.${articleId}.a`).toLowerCase().includes(term);

  const visible = sections
    .map((s) => ({ ...s, articles: s.articles.filter((a) => matches(s.id, a)) }))
    .filter((s) => s.articles.length > 0);

  return (
    <div className="space-y-6">
      <div>
        <span className="label">{t("kb.subtitle")}</span>
        <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tighter mt-1">{t("kb.title")}</h1>
      </div>

      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
        <input data-testid="kb-search" className="input pl-10 py-2.5 text-sm"
               placeholder={t("kb.searchPlaceholder")} value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      {visible.length === 0 && (
        <p className="text-sm text-slate-400 text-center py-10">{t("kb.noResults")}</p>
      )}

      {visible.map((section) => (
        <Panel key={section.id} className="card p-5" testid={`kb-section-${section.id}`}>
          <div className="flex items-center gap-2 mb-1">
            <BookOpen size={17} className="text-brand-500" />
            <h2 className="font-heading font-semibold tracking-tight text-lg">{t(`kb.${section.id}.title`)}</h2>
          </div>
          <div className="mt-2">
            {section.articles.map((a) => <Article key={a} sectionId={section.id} articleId={a} t={t} />)}
          </div>
        </Panel>
      ))}

      {/* The primer sits below the FAQ and outside the search filter: it is a long
          read rather than an answer to a question, and hiding it behind a search term
          nobody would think to type would mean nobody ever finds it. */}
      {!term && <StockPrimer />}

      <Panel className="card p-5 bg-slate-50/60" testid="kb-contact">
        <h3 className="font-medium text-slate-800">{t("kb.stillStuck")}</h3>
        <p className="text-sm text-slate-600 mt-1">{t("kb.stillStuckBody")}</p>
        {features.payments && (
          <Link to="/billing" className="text-sm text-brand-600 hover:underline mt-2 inline-block">
            {t("kb.viewPlans")}
          </Link>
        )}
      </Panel>

      <Disclaimer />
    </div>
  );
}
